import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

/** Icons the public /travel page understands. Keep in sync with TravelSection.jsx. */
const TRAVEL_ICONS = ['Plane', 'Car', 'ParkingCircle', 'Hotel', 'MapPin', 'Clock', 'Bus', 'Train'];

/**
 * AdminTravelManager
 *
 * Edits the /travel page: header, transportation cards (CRUD + reorder,
 * with icon picker), arrival notice, accommodations intro, and hotel list
 * (CRUD + reorder). Public design/layout is preserved.
 */
const AdminTravelManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/travel/content`);
      setInitial(clone(res.data || {}));
      setContent(clone(res.data || {}));
    } catch { setError('Could not load current content.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => (
    content && initial && JSON.stringify(content) !== JSON.stringify(initial)
  ), [content, initial]);

  const patch = (fn) => setContent((prev) => { const n = clone(prev); fn(n); return n; });

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError('');
    try {
      const res = await axios.put(`${API}/admin/travel/content`, content, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/travel/content/reset`, {}, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setResetOpen(false);
    } catch { setError('Failed to reset content.'); }
    finally { setResetting(false); }
  };

  if (loading || !content) {
    return <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading Travel…
    </div>;
  }

  const cards = content.cards || [];
  const hotels = content.hotels || [];

  const setCard = (i, key, val) => patch((d) => { d.cards[i][key] = val; });
  const moveCard = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.cards.length) return;
    [d.cards[i], d.cards[j]] = [d.cards[j], d.cards[i]];
  });
  const removeCard = (i) => patch((d) => { d.cards.splice(i, 1); });
  const addCard = () => patch((d) => { d.cards = d.cards || []; d.cards.push({ icon: 'Plane', title: 'New Card', subtitle: '', description: '' }); });

  const setHotel = (i, key, val) => patch((d) => { d.hotels[i][key] = val; });
  const moveHotel = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.hotels.length) return;
    [d.hotels[i], d.hotels[j]] = [d.hotels[j], d.hotels[i]];
  });
  const removeHotel = (i) => patch((d) => { d.hotels.splice(i, 1); });
  const addHotel = () => patch((d) => { d.hotels = d.hotels || []; d.hotels.push({ name: 'New Hotel', type: 'Comfort Standard', description: '', distance: '' }); });

  return (
    <div className="space-y-4" data-testid="admin-travel">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-[#3B2F2F]">Travel &amp; Accommodations</h2>
          <p className="text-xs text-[#8A7A7A]">Edits go live on the public /travel page.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)} className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1" data-testid="travel-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={() => setContent(clone(initial))} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="travel-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60" data-testid="travel-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-3">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Eyebrow (script line)</label>
            <Input value={content.eyebrow || ''} onChange={(e) => patch((d) => { d.eyebrow = e.target.value; })} data-testid="travel-eyebrow" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
            <Input value={content.title || ''} onChange={(e) => patch((d) => { d.title = e.target.value; })} data-testid="travel-title" />
          </div>
        </div>
      </div>

      {/* Transportation cards */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-[#3B2F2F]">Transportation Cards</h3>
            <p className="text-xs text-[#8A7A7A]">Displayed at the top of /travel.</p>
          </div>
          <Button onClick={addCard} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="travel-add-card">
            <Plus className="w-4 h-4 mr-1" /> Add card
          </Button>
        </div>
        <ul className="space-y-3">
          {cards.map((c, i) => (
            <li key={i} className="grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-3 items-start p-3 rounded-lg border border-[#E5E0D8]" data-testid={`travel-card-row-${i}`}>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Icon</label>
                <Select value={c.icon || 'Plane'} onValueChange={(v) => setCard(i, 'icon', v)}>
                  <SelectTrigger data-testid={`travel-card-icon-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRAVEL_ICONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Input value={c.title || ''} onChange={(e) => setCard(i, 'title', e.target.value)} placeholder="Card title (e.g. By Air)" data-testid={`travel-card-title-${i}`} />
                <Input value={c.subtitle || ''} onChange={(e) => setCard(i, 'subtitle', e.target.value)} placeholder="Bold subtitle" data-testid={`travel-card-subtitle-${i}`} />
                <Textarea rows={2} value={c.description || ''} onChange={(e) => setCard(i, 'description', e.target.value)} placeholder="Short description" data-testid={`travel-card-desc-${i}`} />
              </div>
              <div className="flex md:flex-col items-center gap-1 md:pt-6">
                <button onClick={() => moveCard(i, -1)} disabled={i === 0} className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`travel-card-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => moveCard(i, 1)} disabled={i === cards.length - 1} className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`travel-card-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => removeCard(i)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`travel-card-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Arrival notice + accommodations intro */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Arrival notice</label>
          <Textarea rows={2} value={content.arrivalNotice || ''} onChange={(e) => patch((d) => { d.arrivalNotice = e.target.value; })} data-testid="travel-arrival" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Accommodations intro</label>
          <Textarea rows={3} value={content.accommodationsIntro || ''} onChange={(e) => patch((d) => { d.accommodationsIntro = e.target.value; })} data-testid="travel-accom-intro" />
        </div>
      </div>

      {/* Hotels */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-[#3B2F2F]">Hotels</h3>
            <p className="text-xs text-[#8A7A7A]">Order shown on the public page mirrors this list.</p>
          </div>
          <Button onClick={addHotel} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="travel-add-hotel">
            <Plus className="w-4 h-4 mr-1" /> Add hotel
          </Button>
        </div>
        <ul className="space-y-3">
          {hotels.map((h, i) => (
            <li key={i} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start p-3 rounded-lg border border-[#E5E0D8]" data-testid={`travel-hotel-row-${i}`}>
              <div className="grid md:grid-cols-2 gap-2">
                <Input value={h.name || ''} onChange={(e) => setHotel(i, 'name', e.target.value)} placeholder="Hotel name" data-testid={`travel-hotel-name-${i}`} />
                <Input value={h.type || ''} onChange={(e) => setHotel(i, 'type', e.target.value)} placeholder="Type badge (e.g. Luxury)" data-testid={`travel-hotel-type-${i}`} />
                <Input value={h.distance || ''} onChange={(e) => setHotel(i, 'distance', e.target.value)} placeholder="Distance (e.g. ~10 min from venue)" data-testid={`travel-hotel-distance-${i}`} />
                <Textarea rows={2} value={h.description || ''} onChange={(e) => setHotel(i, 'description', e.target.value)} placeholder="Short description" data-testid={`travel-hotel-desc-${i}`} />
              </div>
              <div className="flex md:flex-col items-center gap-1 md:pt-1">
                <button onClick={() => moveHotel(i, -1)} disabled={i === 0} className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`travel-hotel-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => moveHotel(i, 1)} disabled={i === hotels.length - 1} className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`travel-hotel-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => removeHotel(i)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`travel-hotel-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Travel to defaults?</DialogTitle>
            <DialogDescription>This restores the original transportation cards and hotel list. Cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetDefaults} disabled={resetting} data-testid="travel-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTravelManager;
